/**
 * Generic type helper function because TS doesn't have type inference for generic types
 * This enforces that the `light` and `dark` properties have the same keys
 */
const ComponentThemeMapping = (value) => ({ ...value }); // The `as const` is here and not on the input object outside because it gives more subtle/more readable error messages
/**
 * Generic helper function to generate the final theme objects
 */
const GenerateThemeObject = (value) => Object.fromEntries(Object.entries(value).map((entry) => [kebabize(entry[0]), entry[1]]));
const kebabize = (str) => str.replace(/[A-Z]+(?![a-z])|[A-Z]/g, (letter, ofs) => (ofs ? "-" : "") + letter.toLowerCase());
const colors = {
    neutralWhite: "var(--neutral-white)",
    neutralWhiteFilter: "var(--neutral-white-filter)",
    neutral50: "var(--neutral-50)",
    neutral100: "var(--neutral-100)",
    neutral150: "var(--neutral-150)",
    neutral200: "var(--neutral-200)",
    neutral300: "var(--neutral-300)",
    neutral400: "var(--neutral-400)",
    neutral500: "var(--neutral-500)",
    neutral550: "var(--neutral-550)",
    neutral600: "var(--neutral-600)",
    neutral700: "var(--neutral-700)",
    neutral700Filter: "var(--neutral-700-filter)",
    neutral800: "var(--neutral-800)",
    neutral900: "var(--neutral-900)",
    neutralBlack: "var(--neutral-black)",
    mint100: "var(--mint-100)",
    mint200: "var(--mint-200)",
    blueprint100: "var(--blueprint-100)",
    blueprint200: "var(--blueprint-200)",
    blueprint300: "var(--blueprint-300)",
    blueprint400: "var(--blueprint-400)",
    blueprint400Filter: "var(--blueprint-400-filter)",
    blueprint500: "var(--blueprint-500)",
    blueprint500Filter: "var(--blueprint-500-filter)",
    blueprint600: "var(--blueprint-600)",
    blueprint700: "var(--blueprint-700)",
    yellow100: "var(--yellow-100)",
    yellow200: "var(--yellow-200)",
    yellow300: "var(--yellow-300)",
    yellow400: "var(--yellow-400)",
    yellow500: "var(--yellow-500)",
    yellow600: "var(--yellow-600)",
    yellow700: "var(--yellow-700)",
    red100: "var(--red-100)",
    red200: "var(--red-200)",
    red300: "var(--red-300)",
    red400: "var(--red-400)",
    red500: "var(--red-500)",
    red600: "var(--red-600)",
    red700: "var(--red-700)",
    peach100: "var(--peach-100)",
    peach200: "var(--peach-200)",
    green100: "var(--green-100)",
    green200: "var(--green-200)",
    green300: "var(--green-300)",
    green400: "var(--green-400)",
    green500: "var(--green-500)",
    green600: "var(--green-600)",
    green700: "var(--green-700)",
    neutralWhite_04: "rgb(var(--neutral-white-raw) / 0.04)",
    neutralWhite_05: "rgb(var(--neutral-white-raw) / 0.05)",
    neutralWhite_10: "rgb(var(--neutral-white-raw) / 0.1)",
    neutralWhite_20: "rgb(var(--neutral-white-raw) / 0.2)",
    neutralWhite_32: "rgb(var(--neutral-white-raw) / 0.32)",
    neutralWhite_40: "rgb(var(--neutral-white-raw) / 0.40)",
    neutralWhite_64: "rgb(var(--neutral-white-raw) / 0.64)",
    neutralBlack_2: "rgb(var(--neutral-black-raw) / 0.02)",
    neutralBlack_4: "rgb(var(--neutral-black-raw) / 0.04)",
    neutralBlack_5: "rgb(var(--neutral-black-raw) / 0.05)",
    neutralBlack_10: "rgb(var(--neutral-black-raw) / 0.1)",
    neutralBlack_20: "rgb(var(--neutral-black-raw) / 0.2)",
    neutralBlack_70: "rgb(var(--neutral-black-raw) / 0.7)",
    neutral700_40: "rgb(var(--neutral-700-raw) / 0.4)",
    blueprint500_05: "rgb(var(--blueprint-500-raw) / 0.05)",
    blueprint500_10: "rgb(var(--blueprint-500-raw) / 0.1)",
    blueprint500_40: "rgb(var(--blueprint-500-raw) / 0.4)",
    yellow300_10: "rgb(var(--yellow-300-raw) / 0.1)",
    yellow400_10: "rgb(var(--yellow-400-raw) / 0.1)",
    red500_10: "rgb(var(--red-500-raw) / 0.1)",
    green500_10: "rgb(var(--green-500-raw) / 0.1)",
};
const shadows = {
    shadowLight_xs: "var(--shadow-xs-light)",
    shadowLight_sm: "var(--shadow-sm-light)",
    shadowLight_md: "var(--shadow-md-light)",
    shadowLight_lg: "var(--shadow-lg-light)",
    shadowDark_xs: "var(--shadow-xs-dark)",
    shadowDark_sm: "var(--shadow-sm-dark)",
    shadowDark_md: "var(--shadow-md-dark)",
    shadowDark_lg: "var(--shadow-lg-dark)",
};
const textStyles = ComponentThemeMapping({
    light: {
        textHeaderLabel: colors.neutralBlack,
        textPrimaryLabel: colors.neutral700,
        textPrimaryLabelFilter: colors.neutral700Filter,
        textPrimaryLabelDisabled: colors.neutral700_40,
        textSecondaryLabel: colors.neutral550,
        textTertiaryLabel: colors.neutral500,
        textAccentPrimaryLabel: colors.blueprint500,
        textAccentPrimaryLabelFilter: colors.blueprint500Filter,
        textAccentSecondaryLabel: colors.blueprint600,
        textOnAccentPrimaryLabel: colors.neutralWhite,
        textOnAvatar: colors.neutralWhite,
        textOnTooltip: colors.neutralWhite,
        warningTextFillColor: colors.yellow400,
        errorTextFillColor: colors.red500,
        successTextFillColor: colors.green500,
    },
    dark: {
        textHeaderLabel: colors.neutralWhite,
        textPrimaryLabel: colors.neutralWhite,
        textPrimaryLabelFilter: colors.neutralWhiteFilter,
        textPrimaryLabelDisabled: colors.neutralWhite_40,
        textSecondaryLabel: colors.neutral550,
        textTertiaryLabel: colors.neutral500,
        textAccentPrimaryLabel: colors.blueprint400,
        textAccentPrimaryLabelFilter: colors.blueprint400Filter,
        textAccentSecondaryLabel: colors.blueprint500,
        textOnAccentPrimaryLabel: colors.neutralWhite,
        textOnAvatar: colors.neutralBlack,
        textOnTooltip: colors.neutralBlack,
        warningTextFillColor: colors.yellow300,
        errorTextFillColor: colors.red400,
        successTextFillColor: colors.green400,
    },
});
const iconStyles = ComponentThemeMapping({
    light: {
        logoFillColor: colors.neutralBlack,
        successIconFillColor: textStyles.light.successTextFillColor,
        warningIconFillColor: textStyles.light.warningTextFillColor,
        informationIconFillColor: textStyles.light.textAccentPrimaryLabel,
        errorIconFillColor: textStyles.light.errorTextFillColor,
        primaryIconFillColor: textStyles.light.textPrimaryLabel,
        secondaryIconFillColor: textStyles.light.textTertiaryLabel,
    },
    dark: {
        logoFillColor: colors.neutralWhite,
        successIconFillColor: textStyles.dark.successTextFillColor,
        warningIconFillColor: textStyles.dark.warningTextFillColor,
        informationIconFillColor: textStyles.dark.textAccentPrimaryLabel,
        errorIconFillColor: textStyles.dark.errorTextFillColor,
        primaryIconFillColor: textStyles.dark.textPrimaryLabel,
        secondaryIconFillColor: textStyles.dark.textTertiaryLabel,
    },
});
const controlStyles = ComponentThemeMapping({
    light: {
        controlBackground: colors.neutralWhite_64,
        controlHoverBackground: colors.neutralBlack_2,
        controlPressedBackground: colors.neutralBlack_5,
        controlBackgroundSecondary: colors.neutral400,
        controlBackgroundSecondaryHover: colors.neutral500,
        controlBorder: colors.neutralBlack_10,
        controlAccentBackground: colors.blueprint500,
        controlAttentionBackground: colors.blueprint500_05,
        controlAccentHoverBackground: colors.blueprint600,
        controlAccentPressedBackground: colors.blueprint400,
        controlFocusHighlightStrokeColor: colors.blueprint500_40,
        controlErrorHighlightStrokeColor: colors.red500,
    },
    dark: {
        controlBackground: colors.neutralWhite_10,
        controlHoverBackground: colors.neutralWhite_20,
        controlPressedBackground: colors.neutralWhite_32,
        controlBackgroundSecondary: colors.neutral550,
        controlBackgroundSecondaryHover: colors.neutral600,
        controlBorder: colors.neutralBlack_20,
        controlAccentBackground: colors.blueprint400,
        controlAttentionBackground: colors.blueprint500_05,
        controlAccentHoverBackground: colors.blueprint500,
        controlAccentPressedBackground: colors.blueprint300,
        controlFocusHighlightStrokeColor: colors.blueprint500_40,
        controlErrorHighlightStrokeColor: colors.red500,
    },
});
const backgroundStyles = ComponentThemeMapping({
    light: {
        pagePrimaryBackground: colors.neutralWhite,
        pageSecondaryBackground: colors.neutral50,
        pageTertiaryBackground: colors.neutral150,
        dialogBackground: colors.neutralWhite,
        popoverBackground: colors.neutralWhite,
        avatarBackgroundGray: colors.neutral600,
        avatarBackgroundMint: colors.mint200,
        avatarBackgroundYellow: colors.yellow200,
        avatarBackgroundPeach: colors.peach200,
        avatarBackgroundGreen: colors.green300,
        informationBackground: colors.neutralBlack_4,
        attentionBackground: colors.blueprint500_10,
        warningBackground: colors.yellow300_10,
        criticalBackground: colors.red500_10,
        successBackground: colors.green500_10,
        fieldBackground: colors.neutralWhite,
        fieldBackgroundSecondary: colors.neutralBlack_5,
        snackbarBackground: colors.neutralBlack_5,
        tooltipBackground: colors.neutralBlack,
    },
    dark: {
        pagePrimaryBackground: colors.neutral700,
        pageSecondaryBackground: colors.neutral800,
        pageTertiaryBackground: colors.neutralBlack,
        dialogBackground: colors.neutral700,
        popoverBackground: colors.neutral700,
        avatarBackgroundGray: colors.neutral600,
        avatarBackgroundMint: colors.mint100,
        avatarBackgroundYellow: colors.yellow100,
        avatarBackgroundPeach: colors.peach100,
        avatarBackgroundGreen: colors.green200,
        informationBackground: colors.neutralWhite_04,
        attentionBackground: colors.blueprint500_10,
        warningBackground: colors.yellow400_10,
        criticalBackground: colors.red500_10,
        successBackground: colors.green500_10,
        fieldBackground: colors.neutralBlack_10,
        fieldBackgroundSecondary: colors.neutralBlack_10,
        snackbarBackground: colors.neutralWhite_05,
        tooltipBackground: colors.neutral400,
    },
});
const shadowStyles = ComponentThemeMapping({
    light: {
        shadowXs: shadows.shadowLight_xs,
        shadowSm: shadows.shadowLight_sm,
        shadowMd: shadows.shadowLight_md,
        shadowLg: shadows.shadowLight_lg,
    },
    dark: {
        shadowXs: shadows.shadowDark_xs,
        shadowSm: shadows.shadowDark_sm,
        shadowMd: shadows.shadowDark_md,
        shadowLg: shadows.shadowDark_lg,
    },
});
const separatorStyles = ComponentThemeMapping({
    light: {
        separatorOpaque: colors.neutral150,
        separator: colors.neutralBlack_10,
    },
    dark: {
        separatorOpaque: colors.neutral700,
        separator: colors.neutralWhite_10,
    },
});
const backdropStyles = ComponentThemeMapping({
    light: {
        backdrop: colors.neutralBlack_70,
    },
    dark: {
        backdrop: colors.neutralBlack_70,
    },
});
const borders = ComponentThemeMapping({
    light: {
        fieldStroke: colors.neutralBlack_10,
    },
    dark: {
        fieldStroke: colors.neutralBlack_20,
    },
});
const lightTheme = GenerateThemeObject({
    ...textStyles.light,
    ...controlStyles.light,
    ...iconStyles.light,
    ...backgroundStyles.light,
    ...separatorStyles.light,
    ...backdropStyles.light,
    ...borders.light,
    ...shadowStyles.light,
});
const darkTheme = GenerateThemeObject({
    ...textStyles.dark,
    ...controlStyles.dark,
    ...iconStyles.dark,
    ...backgroundStyles.dark,
    ...separatorStyles.dark,
    ...backdropStyles.dark,
    ...borders.dark,
    ...shadowStyles.dark,
});

export { darkTheme, lightTheme };
//# sourceMappingURL=themes.js.map
